/* eslint no-console: 0 */
/* global axios */
import ApiClient from '../ApiClient';

const positiveIntegerPathSegment = (value, name) => {
  const normalizedValue = String(value ?? '').trim();
  if (!/^[1-9]\d*$/.test(normalizedValue)) {
    throw new TypeError(`${name} must be a positive integer`);
  }
  return encodeURIComponent(normalizedValue);
};

export const buildCreatePayload = ({
  message,
  isPrivate,
  contentAttributes,
  echoId,
  files,
  ccEmails = '',
  bccEmails = '',
  toEmails = '',
  templateParams,
  isVoiceMessage = false,
}) => {
  let payload;
  if (files && files.length !== 0) {
    payload = new FormData();
    if (message) {
      payload.append('content', message);
    }
    files.forEach(file => {
      payload.append('attachments[]', file);
    });
    payload.append('private', isPrivate);
    payload.append('echo_id', echoId);
    payload.append('cc_emails', ccEmails);
    payload.append('bcc_emails', bccEmails);

    if (toEmails) {
      payload.append('to_emails', toEmails);
    }
    if (contentAttributes) {
      payload.append('content_attributes', JSON.stringify(contentAttributes));
    }
    if (isVoiceMessage) {
      payload.append('is_voice_message', true);
    }
  } else {
    payload = {
      content: message,
      private: isPrivate,
      echo_id: echoId,
      content_attributes: contentAttributes,
      cc_emails: ccEmails,
      bcc_emails: bccEmails,
      to_emails: toEmails,
      template_params: templateParams,
    };
  }
  return payload;
};

class MessageApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  create(
    {
      conversationId,
      message,
      private: isPrivate,
      contentAttributes,
      echo_id: echoId,
      files,
      ccEmails = '',
      bccEmails = '',
      toEmails = '',
      templateParams,
      isVoiceMessage = false,
    },
    axiosConfig = {}
  ) {
    const requestConfig = {};
    if (axiosConfig.signal) requestConfig.signal = axiosConfig.signal;
    if (axiosConfig.headers) requestConfig.headers = axiosConfig.headers;
    const hasExplicitAccountId = Object.prototype.hasOwnProperty.call(
      axiosConfig,
      'accountId'
    );
    const conversationPath = positiveIntegerPathSegment(
      conversationId,
      'conversationId'
    );
    const conversationUrl = hasExplicitAccountId
      ? `/api/v1/accounts/${positiveIntegerPathSegment(
          axiosConfig.accountId,
          'accountId'
        )}/conversations`
      : this.url;

    return axios({
      method: 'post',
      url: `${conversationUrl}/${conversationPath}/messages`,
      data: buildCreatePayload({
        message,
        isPrivate,
        contentAttributes,
        echoId,
        files,
        ccEmails,
        bccEmails,
        toEmails,
        templateParams,
        isVoiceMessage,
      }),
      ...requestConfig,
    });
  }

  delete(conversationID, messageId) {
    return axios.delete(`${this.url}/${conversationID}/messages/${messageId}`);
  }

  retry(conversationID, messageId) {
    return axios.post(
      `${this.url}/${conversationID}/messages/${messageId}/retry`
    );
  }

  getPreviousMessages({ conversationId, after, before }) {
    const params = { before };
    if (after && Number(after) !== Number(before)) {
      params.after = after;
    }
    return axios.get(`${this.url}/${conversationId}/messages`, { params });
  }

  translateMessage(conversationId, messageId, targetLanguage) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/translate`,
      {
        target_language: targetLanguage,
      }
    );
  }

  // eslint-disable-next-line class-methods-use-this
  forward(accountId, messageIds, conversationIds) {
    return axios.post(`/api/v1/accounts/${accountId}/messages/forwards`, {
      message_ids: messageIds,
      conversation_ids: conversationIds,
    });
  }

  react(conversationId, messageId, emoji) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/react`,
      { emoji }
    );
  }

  edit(conversationId, messageId, content) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/edit`,
      { content }
    );
  }

  forwardContact(conversationId, contactId) {
    return axios.post(
      `${this.url}/${conversationId}/messages/forward_contact`,
      { contact_id: contactId }
    );
  }
}

export default new MessageApi();
